import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAward } from '@fortawesome/free-solid-svg-icons';
import { BADGE_ICON } from '../Competitions/BadgeShelf';
import './TeacherCard.css';

/* =========================================================
   One teacher card of "Meet our teachers" (home page).
   The same card is the live preview in the teacher's Settings and in Admin › Teacher profiles,
   so what they see is exactly what the home page shows. Always the dark site look.
   t = { name, photo, initial, studies: [{ level, field, status }], about, years,
         badges: [{ key, title, icon, count }] (up to 3 public badges, added by themselves) }
   ========================================================= */

export const LEVELS = [
  ['bachelor', 'Bachelor’s'],
  ['engineering', 'Engineering'],
  ['master', 'Master’s'],
  ['phd', 'PhD'],
];

export const DEFAULT_FIELD = { bachelor: 'Computer Science', engineering: 'Computer Engineering', master: 'Computer Science', phd: 'Computer Science' };

/* "Computer Science graduate", "Master's student in AI", "PhD in Robotics"… */
export const studyText = (s) => {
  const field = String(s?.field || '').trim();
  if (!field || !s?.level) return '';
  const studying = s.status === 'studying';
  if (s.level === 'master') return studying ? `Master’s student in ${field}` : `Master’s in ${field}`;
  if (s.level === 'phd') return studying ? `PhD student in ${field}` : `PhD in ${field}`;
  return studying ? `${field} student` : `${field} graduate`; // bachelor / engineering
};

export const studiesLine = (studies = []) => studies.map(studyText).filter(Boolean).join(' · ');

/* up to 3 badge icons; tap one to see its name */
const Badges = ({ list }) => {
  const [open, setOpen] = useState(null);
  if (!list?.length) return null;
  const shown = list.find((b) => b.key === open);
  return (
    <div className="rmtcard-badges">
      <div className="rmtcard-badge-row">
        {list.map((b) => (
          <button
            key={b.key}
            type="button"
            className={`rmtcard-badge is-${b.key} ${open === b.key ? 'is-on' : ''}`}
            onClick={() => setOpen((k) => (k === b.key ? null : b.key))}
            title={b.count > 1 ? `${b.title} ×${b.count}` : b.title}
            aria-label={b.count > 1 ? `${b.title}, ${b.count} times` : b.title}
            aria-pressed={open === b.key}
          >
            <FontAwesomeIcon icon={BADGE_ICON[b.icon] || faAward} />
            {b.count > 1 && <b>×{b.count}</b>}
          </button>
        ))}
      </div>
      {shown && (
        <span className="rmtcard-badge-name" role="status">
          {shown.title}
          {shown.count > 1 ? ` ×${shown.count}` : ''}
        </span>
      )}
    </div>
  );
};

const TeacherCard = ({ t, as: Tag = 'div' }) => {
  const [broken, setBroken] = useState(false);
  const line = studiesLine(t.studies);
  return (
    <Tag className="rmtcard">
      <span className="rmtcard-face" aria-hidden="true">
        {t.photo && !broken ? <img src={t.photo} alt="" loading="lazy" onError={() => setBroken(true)} /> : <span>{t.initial}</span>}
      </span>
      <strong className="rmtcard-name">{t.name}</strong>
      {line && <span className="rmtcard-study">{line}</span>}
      {t.years ? <span className="rmtcard-years">{t.years === 1 ? '1 year' : `${t.years} years`} at RoboMind</span> : null}
      <Badges list={t.badges} />
      {t.about && (
        <p className="rmtcard-about" dir="auto">
          {t.about}
        </p>
      )}
    </Tag>
  );
};

export default TeacherCard;
